"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Home,
} from "lucide-react";
import { BookingStep } from "@/types/booking";
import { formatDuration, formatPrice, getEffectiveTotalDurationMinutes, getBusinessCurrency } from "@/lib/utils";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import {
  addMinutesToScheduledWall,
  escapeIcsText,
  formatRealInstantForCalendar,
  formatScheduledDate,
  formatScheduledTime,
  formatScheduledWallForCalendar,
  isValidTimeZone,
  parseScheduledWall,
  scheduledWallRangeToRealInstants,
} from "@/lib/salon-time";

export default function Success() {
  useScrollToTop();

  const {
    selectedServices,
    selectedProducts,
    appointmentDateTime,
    businessInfo,
    selectedSalon,
    serviceClient,
    bookedAppointment,
    handleNextStep,
    resetBooking,
    priceSummary,
    appointmentPaymentType,
    actualProcessingFee,
    actualAmountPaid,
    addToast,
  } = useBooking();

  // Calculate total duration
  const effectiveScheduleType =
    selectedServices.length > 0 &&
    selectedServices.every(
      (s: any) => String(s?.bundleScheduleType || "sequence").toLowerCase() === "parallel"
    )
      ? "parallel"
      : "sequence";
  const totalDuration = getEffectiveTotalDurationMinutes({
    scheduleType: effectiveScheduleType,
    services: selectedServices,
  });
  const appointmentStartAt = bookedAppointment?.startAt ?? appointmentDateTime;
  const appointmentTimezone =
    bookedAppointment?.timezone ?? selectedSalon?.timezone;
  let appointmentEndAt = bookedAppointment?.endAt ?? null;
  if (!appointmentEndAt && appointmentStartAt) {
    try {
      appointmentEndAt = addMinutesToScheduledWall(
        appointmentStartAt,
        totalDuration,
      );
    } catch {
      appointmentEndAt = null;
    }
  }
  const calendarReady = Boolean(
    appointmentStartAt &&
      appointmentEndAt &&
      isValidTimeZone(appointmentTimezone) &&
      parseScheduledWall(appointmentStartAt) &&
      parseScheduledWall(appointmentEndAt),
  );
  const servicesTotal = selectedServices.reduce((sum, service) => {
    const price = service.bundleId
      ? service.price || 0
      : service.staffPrice || service.price || 0;
    const quantity = service.quantity || 1;
    return sum + price * quantity;
  }, 0);
  const productsTotal = selectedProducts.reduce((sum, product) => {
    return sum + (product.retailPrice || 0) * (product.quantity || 1);
  }, 0);
  const totalPrice = servicesTotal + productsTotal;
  const calendarDetails = [
    ...selectedServices.map((service) => service.name),
    ...selectedProducts.map((product) =>
      (product.quantity || 1) > 1
        ? `${product.name} x${product.quantity}`
        : product.name,
    ),
  ]
    .filter(Boolean)
    .join(", ");
  const currency = getBusinessCurrency(businessInfo);
  const subtotalPaid =
    actualAmountPaid !== null && actualProcessingFee !== null
      ? Math.max(actualAmountPaid - actualProcessingFee, 0)
      : appointmentPaymentType === "deposit_payment" && priceSummary?.depositAmount
        ? priceSummary.depositAmount
        : priceSummary?.subTotal || totalPrice;

  const handleNewBooking = () => {
    resetBooking();
    handleNextStep(BookingStep.LANDING);
  };

  return (
    <div className="min-h-screen bg-grey-5">
      {/* Success Header */}
      <div className="bg-green-50 border-b border-green-200">
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-green-300/10 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-grey-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-grey-600">
            Your appointment has been successfully booked
          </p>
          {bookedAppointment?.bookingId && (
            <p className="text-sm text-grey-500 mt-2">
              Booking ID: {bookedAppointment.bookingId}
            </p>
          )}
        </div>
      </div>

      {/* Booking Details */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Appointment Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Appointment Details
          </h2>

          <div className="space-y-4">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">
                  {appointmentStartAt
                    ? formatScheduledDate(appointmentStartAt) || "Date unavailable"
                    : "Date unavailable"}
                </p>
                <p className="text-sm text-grey-600">
                  {appointmentStartAt
                    ? formatScheduledTime(appointmentStartAt)
                    : ""}
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">
                  {businessInfo?.name}
                </p>
                <p className="text-sm text-grey-600">
                  {selectedSalon?.address}
                  {selectedSalon?.city && `, ${selectedSalon.city}`}
                  {selectedSalon?.state && `, ${selectedSalon.state}`}
                </p>
                {selectedSalon?.phone && (
                  <p className="text-sm text-grey-600">
                    Tel: {selectedSalon.callingCode || "+234"}{" "}
                    {selectedSalon.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">Duration</p>
                <p className="text-sm text-grey-600">
                  {formatDuration(totalDuration)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            {selectedProducts.length > 0 ? "Items" : "Services Booked"}
          </h2>

          <div className="space-y-3">
            {selectedProducts.map((product) => (
              <div
                key={product.id}
                className="flex justify-between items-start pb-3 border-b border-grey-100 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-grey-900">
                    {product.name}
                    {(product.quantity || 1) > 1 ? ` x${product.quantity}` : ""}
                  </p>
                  <p className="text-sm text-grey-600 mt-1">Product</p>
                </div>
                <p className="font-medium text-grey-900">
                  {formatPrice(
                    (product.retailPrice || 0) * (product.quantity || 1),
                    currency
                  )}
                </p>
              </div>
            ))}
            {selectedServices.map((service) => (
              <div
                key={service.id}
                className="flex justify-between items-start pb-3 border-b border-grey-100 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-grey-900">{service.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {service.staff && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-grey-400" />
                        <span className="text-sm text-grey-600">
                          {service.staff.user?.firstName}{" "}
                          {service.staff.user?.lastName}
                        </span>
                      </div>
                    )}
                    <span className="text-sm text-grey-600">
                      {formatDuration((service.duration || 0) + (service.extraTime || 0))}
                    </span>
                  </div>
                </div>
                <p className="font-medium text-grey-900">
                  {formatPrice(
                    (service.bundleId
                      ? service.price || 0
                      : service.staffPrice || service.price || 0) *
                      (service.quantity || 1),
                    currency
                  )}
                </p>
              </div>
            ))}

            {/* Processing Fee - only shown when a fee was actually charged */}
            {actualProcessingFee && actualProcessingFee > 0 && appointmentPaymentType !== "unpaid" && (
              <div className="flex justify-between items-center pt-3 border-t border-grey-100">
                <span className="text-sm text-grey-600">
                  Subtotal
                </span>
                <span className="text-sm text-grey-900">
                  {formatPrice(
                    subtotalPaid,
                    currency
                  )}
                </span>
              </div>
            )}
            {actualProcessingFee && actualProcessingFee > 0 && appointmentPaymentType !== "unpaid" && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-grey-600">
                  Processing fee
                </span>
                <span className="text-sm text-grey-900">
                  {formatPrice(
                    actualProcessingFee,
                    currency
                  )}
                </span>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center pt-3">
              <span className="text-lg font-semibold text-grey-900">
                {appointmentPaymentType === "unpaid" ? "Amount Due" : "Total Paid"}
              </span>
              <span className={`text-xl font-bold ${appointmentPaymentType === "unpaid" ? "text-grey-900" : "text-green-600"}`}>
                {formatPrice(
                  appointmentPaymentType === "unpaid"
                    ? totalPrice
                    : actualAmountPaid
                      ? actualAmountPaid
                      : appointmentPaymentType === "deposit_payment" && priceSummary?.depositAmount
                        ? priceSummary.depositAmount
                        : priceSummary?.totalPaid || totalPrice,
                  currency
                )}
              </span>
            </div>
            {appointmentPaymentType === "unpaid" && (
              <p className="text-sm text-grey-600 mt-2">
                Payment will be collected at the venue
              </p>
            )}
          </div>
        </div>

        {/* Customer Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Customer Information
          </h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Name</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.firstName} {serviceClient?.lastName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Email</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Phone</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.callingCode} {serviceClient?.phone}
                </p>
              </div>
            </div>

            {serviceClient?.address && (
              <div className="flex items-center gap-3">
                <Home className="h-5 w-5 text-grey-400" />
                <div>
                  <p className="text-sm text-grey-600">Address</p>
                  <p className="font-medium text-grey-900">
                    {serviceClient.address}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleNewBooking}
            className="flex-1 bg-green-300 hover:bg-green-400 text-white"
          >
            Book Another Appointment
          </Button>
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex-1 border-grey-300"
          >
            Print Confirmation
          </Button>
        </div>

        {/* Add to Calendar */}
        <div className="text-center">
          <p className="text-sm text-grey-600 mb-2">Add to your calendar</p>
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={!calendarReady}
              onClick={() => {
                if (
                  !appointmentStartAt ||
                  !appointmentEndAt ||
                  !isValidTimeZone(appointmentTimezone)
                ) return;
                const startDate = formatScheduledWallForCalendar(appointmentStartAt);
                const endDate = formatScheduledWallForCalendar(appointmentEndAt);
                const location = [
                  selectedSalon?.address,
                  selectedSalon?.city,
                  selectedSalon?.state,
                ]
                  .filter(Boolean)
                  .join(", ");
                const params = new URLSearchParams({
                  action: "TEMPLATE",
                  text: `Appointment at ${businessInfo?.name}`,
                  dates: `${startDate}/${endDate}`,
                  ctz: appointmentTimezone,
                  details: calendarDetails,
                  location,
                });

                window.open(
                  `https://calendar.google.com/calendar/render?${params.toString()}`,
                  "_blank"
                );
              }}
            >
              Google Calendar
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={!calendarReady}
              onClick={() => {
                if (
                  !appointmentStartAt ||
                  !appointmentEndAt ||
                  !isValidTimeZone(appointmentTimezone)
                ) return;
                const location = [
                  selectedSalon?.address,
                  selectedSalon?.city,
                  selectedSalon?.state,
                ]
                  .filter(Boolean)
                  .join(", ");
                const summary = `Appointment at ${businessInfo?.name}`;
                const uid = `${bookedAppointment?.id || bookedAppointment?.bookingId || Date.now()}@withsplice.com`;
                let icsContent: string;
                try {
                  const { startInstant, endInstant } =
                    scheduledWallRangeToRealInstants(
                      appointmentStartAt,
                      appointmentEndAt,
                      appointmentTimezone,
                    );
                  icsContent = [
                    "BEGIN:VCALENDAR",
                    "VERSION:2.0",
                    "PRODID:-//Splice//Booking//EN",
                    "CALSCALE:GREGORIAN",
                    "BEGIN:VEVENT",
                    `UID:${uid}`,
                    `DTSTAMP:${formatRealInstantForCalendar(new Date())}`,
                    `DTSTART:${formatRealInstantForCalendar(startInstant)}`,
                    `DTEND:${formatRealInstantForCalendar(endInstant)}`,
                    `SUMMARY:${escapeIcsText(summary)}`,
                    `DESCRIPTION:${escapeIcsText(calendarDetails)}`,
                    `LOCATION:${escapeIcsText(location)}`,
                    "END:VEVENT",
                    "END:VCALENDAR",
                    "",
                  ].join("\r\n");
                } catch {
                  addToast({
                    message: "Unable to export this appointment time.",
                    variant: "error",
                  });
                  return;
                }

                // Safari-compatible download method
                const isSafari = /^((?!chrome|android).)*safari/i.test(
                  navigator.userAgent
                );

                if (isSafari) {
                  // For Safari, use data URI approach
                  const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(
                    icsContent
                  )}`;
                  const a = document.createElement("a");
                  a.href = dataUri;
                  a.download = "appointment.ics";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                } else {
                  // For other browsers, use blob URL
                  const blob = new Blob([icsContent], {
                    type: "text/calendar;charset=utf-8",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "appointment.ics";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }
              }}
            >
              Download .ics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
